import React, { useCallback, useMemo } from 'react'
import { ALL_STATES } from 'src/participant/participantConstants'
import { usePanelPatientUid } from '../../dashboard/panelContext'
import { CardFooter, FieldRow, FieldSection, LabeledSelect, LabeledText, noop } from '../fieldPrimitives'
import { LOCATION_CREDENTIALING_SCHEMA } from './patientRecordSchema'
import { makeFieldSetter, useCardAutosave } from './useCardAutosave'
import { usePatientRecord } from './usePatientRecord'
import { useUpdatePatientRecord } from './useUpdatePatientRecord'

type LocationValues = { stateOfResidence: string }

const STATE_OPTIONS = ALL_STATES.map(({ code, name }) => ({ value: code, label: `${name} (${code})` }))

const LocationCredentialingPanel = () => {
  const uid = usePanelPatientUid()
  const { loading, patient } = usePatientRecord(uid)
  const update = useUpdatePatientRecord(uid)

  // `''` is the "unset" convention here; a sentinel string would render unselectable in the dropdown.
  const initial = useMemo<LocationValues>(
    () => ({ stateOfResidence: patient?.stateOfResidence ?? '' }),
    [patient?.stateOfResidence]
  )

  const save = useCallback(
    (next: LocationValues) => update({ stateOfResidence: next.stateOfResidence || null }, undefined),
    [update]
  )

  const { values, setValues, error, status } = useCardAutosave(initial, LOCATION_CREDENTIALING_SCHEMA, save)
  const set = makeFieldSetter(setValues)

  return (
    <FieldSection>
      <FieldRow columns={2}>
        <LabeledSelect
          label="Physical location"
          value={values.stateOfResidence}
          onChange={set('stateOfResidence')}
          options={STATE_OPTIONS}
        />
        <LabeledText
          label="Required health plan credentialing"
          value={patient?.credentialingHealthPlan ?? ''}
          onChange={noop}
          disabled
        />
      </FieldRow>
      <CardFooter loading={loading} status={status} error={error} />
    </FieldSection>
  )
}

export default LocationCredentialingPanel
